// A masked KuikaSvg paints its icon as one flat shape, so it cannot trace a stroke. Drawing an
// icon on means rendering its paths inline — this module turns an icon file into a tree that can
// be rendered that way, and only that way.
//
// The icons are our own static assets, but the output is still built from a whitelist rather than
// injected as markup: only shape elements and their geometry survive. Ids go (a second copy on the
// page would collide with the first), and so do defs, clip paths, filters and masks — the Tabler
// exports wrap every icon in a clip-path the size of its own viewBox, which draws nothing.

const SHAPE_TAGS: ReadonlySet<string> = new Set<string>([
  "path",
  "circle",
  "rect",
  "line",
  "polyline",
  "polygon",
  "ellipse"
]);

const CONTAINER_TAGS: ReadonlySet<string> = new Set<string>(["svg", "g"]);

/** SVG attribute → React prop. Anything not listed is dropped. */
const GEOMETRY_ATTRIBUTES: Record<string, string> = {
  d: "d",
  cx: "cx",
  cy: "cy",
  r: "r",
  rx: "rx",
  ry: "ry",
  x: "x",
  y: "y",
  width: "width",
  height: "height",
  x1: "x1",
  y1: "y1",
  x2: "x2",
  y2: "y2",
  points: "points",
  transform: "transform",
  "stroke-width": "strokeWidth",
  "stroke-linecap": "strokeLinecap",
  "stroke-linejoin": "strokeLinejoin",
  "fill-rule": "fillRule",
  "clip-rule": "clipRule"
};

/**
 * The length every traced shape is normalised to. It matches the kit's `kuika-draw` convention, so
 * one dash pattern draws a short tick and a long outline in the same time.
 */
export const DRAWABLE_PATH_LENGTH: number = 24;

/** How a shape comes on: a stroke is traced, a filled shape (which has no line to trace) fades in. */
export type DrawableSvgShapeKind = "stroke" | "fill";

export interface IDrawableSvgShape {
  tag: string;
  kind: DrawableSvgShapeKind;
  props: Record<string, string>;
}

export interface IDrawableSvg {
  viewBox: string;
  /** Flat, in paint order: groups are dissolved once their inherited paint has been resolved. */
  shapes: IDrawableSvgShape[];
}

interface IInheritedPaint {
  stroke: boolean;
  fill: boolean;
}

const isPainted = (value: string | null, inherited: boolean): boolean => {
  if (value === null) {
    return inherited;
  }

  return value.trim().toLowerCase() !== "none";
};

const collectShapes = (element: Element, paint: IInheritedPaint, shapes: IDrawableSvgShape[]): void => {
  const tag: string = element.tagName.toLowerCase();
  const ownPaint: IInheritedPaint = {
    stroke: isPainted(element.getAttribute("stroke"), paint.stroke),
    fill: isPainted(element.getAttribute("fill"), paint.fill)
  };

  if (CONTAINER_TAGS.has(tag)) {
    for (const child of Array.from(element.children)) {
      collectShapes(child, ownPaint, shapes);
    }
    return;
  }

  if (!SHAPE_TAGS.has(tag) || (!ownPaint.stroke && !ownPaint.fill)) {
    return;
  }

  const props: Record<string, string> = {};
  for (const attribute of Array.from(element.attributes)) {
    const propName: string | undefined = GEOMETRY_ATTRIBUTES[attribute.name];
    if (propName !== undefined) {
      props[propName] = attribute.value;
    }
  }

  // Colour always comes from the element's `color`, so the icon follows whatever tone it sits in.
  props.stroke = ownPaint.stroke ? "currentColor" : "none";
  props.fill = ownPaint.fill ? "currentColor" : "none";

  const kind: DrawableSvgShapeKind = ownPaint.stroke ? "stroke" : "fill";
  if (kind === "stroke") {
    props.pathLength = String(DRAWABLE_PATH_LENGTH);
  }

  shapes.push({ tag, kind, props });
};

/**
 * Parses an icon file into drawable shapes. Returns null for anything that is not an SVG with a
 * viewBox and at least one visible shape — the caller then falls back to the masked icon.
 */
export const toDrawableSvg = (markup: string): IDrawableSvg | null => {
  const parsed: Document = new DOMParser().parseFromString(markup, "image/svg+xml");
  const root: Element = parsed.documentElement;

  if (root.tagName.toLowerCase() !== "svg" || root.getElementsByTagName("parsererror").length > 0) {
    return null;
  }

  const viewBox: string | null = root.getAttribute("viewBox");
  if (viewBox === null || viewBox.trim() === "") {
    return null;
  }

  // An SVG's initial paint is fill black, stroke none; the root's own attributes override it.
  const shapes: IDrawableSvgShape[] = [];
  collectShapes(root, { stroke: false, fill: true }, shapes);

  return shapes.length > 0 ? { viewBox, shapes } : null;
};

const drawableSvgCache: Map<string, Promise<IDrawableSvg | null>> = new Map<string, Promise<IDrawableSvg | null>>();

const fetchDrawableSvg = async (url: string): Promise<IDrawableSvg | null> => {
  try {
    const response: Response = await fetch(url);
    if (!response.ok) {
      return null;
    }

    const markup: string = await response.text();
    return toDrawableSvg(markup);
  } catch {
    return null;
  }
};

/**
 * Loads an icon once per URL for the page's lifetime: every badge, every reopen and StrictMode's
 * double mount share one request. It is the same URL the masked icon uses, so the HTTP cache is
 * usually warm already. Resolves null on any failure and never rejects; a failure is not cached, so
 * the next mount tries again.
 */
export const loadDrawableSvg = (url: string): Promise<IDrawableSvg | null> => {
  const cached: Promise<IDrawableSvg | null> | undefined = drawableSvgCache.get(url);
  if (cached !== undefined) {
    return cached;
  }

  const pending: Promise<IDrawableSvg | null> = fetchDrawableSvg(url).then(
    (drawable: IDrawableSvg | null): IDrawableSvg | null => {
      if (drawable === null) {
        drawableSvgCache.delete(url);
      }
      return drawable;
    }
  );
  drawableSvgCache.set(url, pending);
  return pending;
};
